import { closestCenter, DndContext } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, useTheme } from '@mui/material';
import React from 'react';

import { ReportPickerMenu } from './ReportPickerMenu';
import { ReportTabsProps } from './ReportTabs.props';
import { DEFAULT_TAB_ID } from './reportTabsHelpers';
import { SetDefaultConfirmDialog } from './SetDefaultConfirmDialog';
import { SortableTab } from './SortableTab';
import { TabContextMenu } from './TabContextMenu';
import { useReportTabs } from './useReportTabs';

const DEFAULT_TAB_LABEL = 'All Open Jobs';

export const ReportTabs: React.FC<ReportTabsProps> = (props) => {
  const {
    activeReport,
    reports,
    recentCodes,
    onReportChange,
    onSave,
    defaultTabLabel,
    onSetDefault,
  } = props;
  const allLabel = defaultTabLabel ?? DEFAULT_TAB_LABEL;
  const theme = useTheme();

  const {
    orderedPinned,
    tabIds,
    currentDefault,
    hasDefaultReport,
    sensors,
    handleDragEnd,
    menuAnchor,
    setMenuAnchor,
    handlePin,
    openSetDefault,
    openRemoveDefault,
    ctxMenu,
    openCtx,
    setCtxMenu,
    ctxItems,
    confirmReport,
    confirmAction,
    setConfirmReport,
    handleConfirmDefault,
    closeCodes,
  } = useReportTabs(props);

  const renderTab = (id: string) => {
    if (id === DEFAULT_TAB_ID) {
      return (
        <SortableTab
          key={id}
          id={id}
          label={allLabel}
          isActive={activeReport === null}
          onSelect={() => onReportChange(null)}
          onContextMenu={(e) => openCtx(e, id)}
          badgeVariant={hasDefaultReport ? 'report' : 'system'}
          badgeLabel={hasDefaultReport ? 'Default report' : 'System default view'}
        />
      );
    }
    const report = orderedPinned.find((r) => r.code === id);
    if (!report) return null;
    return (
      <SortableTab
        key={id}
        id={id}
        label={report.name}
        isActive={activeReport === report.code}
        onSelect={() => onReportChange(report.code)}
        onContextMenu={(e) => openCtx(e, report.code)}
        onClose={() => closeCodes([report.code])}
        closeLabel={`Close ${report.name} tab`}
      />
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          borderBottom: `1px solid ${theme.palette.v4.border}`,
          bgcolor: theme.palette.v4.surface,
        }}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tabIds} strategy={horizontalListSortingStrategy}>
            <Box role="tablist" sx={{ display: 'flex', alignItems: 'stretch' }}>
              {tabIds.map(renderTab)}
            </Box>
          </SortableContext>
        </DndContext>

        <IconButton
          size="small"
          aria-label="Add report tab"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            mx: 0.5,
            alignSelf: 'center',
            color: 'text.secondary',
            '&:hover': { color: theme.palette.v4.accent },
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <ReportPickerMenu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          reports={reports}
          recentCodes={recentCodes}
          onClose={() => setMenuAnchor(null)}
          onPin={handlePin}
          onSetDefault={onSetDefault ? openSetDefault : undefined}
          onRemoveDefault={onSetDefault ? openRemoveDefault : undefined}
        />

        <Box sx={{ flex: 1 }} />

        {onSave && (
          <Button
            size="small"
            startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
            onClick={onSave}
            aria-label="Save report"
            sx={{
              mr: 1,
              alignSelf: 'center',
              textTransform: 'none',
              color: 'text.secondary',
              fontSize: '0.8125rem',
              '&:hover': { color: theme.palette.v4.accent },
            }}
          >
            Save
          </Button>
        )}
      </Box>

      <TabContextMenu
        anchorPosition={ctxMenu?.pos ?? null}
        onClose={() => setCtxMenu(null)}
        items={ctxItems}
      />

      <SetDefaultConfirmDialog
        report={confirmReport}
        action={confirmAction}
        currentDefault={currentDefault}
        onCancel={() => setConfirmReport(null)}
        onConfirm={handleConfirmDefault}
      />
    </>
  );
};
